<template>
  <div class="card">
    <div class="flex justify-between items-center mb-2">
      <h2 class="text-base font-semibold">{{ isEditing ? 'Edit Template' : 'Create Template' }}</h2>
      <div class="flex gap-1.5">
        <Button label="Cancel" icon="pi pi-times" text size="small" @click="$emit('cancel')" />
        <Button label="Save" icon="pi pi-check" size="small" @click="save" :loading="saving" />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
      <div class="flex flex-col gap-2">
        <label class="font-semibold text-sm">Template Name</label>
        <InputText v-model="form.name" placeholder="e.g. Science Results 2024" />
      </div>
      <div class="flex flex-col gap-2">
        <label class="font-semibold text-sm">Description</label>
        <InputText v-model="form.description" placeholder="Description..." />
      </div>
      <div class="flex flex-col gap-2 md:col-span-2">
        <div class="flex items-center gap-2">
          <InputSwitch inputId="use-course-abbrev" v-model="form.useCourseAbbreviationInHeaders" />
          <label for="use-course-abbrev" class="font-semibold text-sm cursor-pointer">Use Course Abbreviation/Code in Headers</label>
        </div>
        <small class="text-gray-500">If enabled, the broadsheet will display the course code/abbreviation instead of the full title.</small>
      </div>
    </div>

    <!-- Help / Hints Section -->
    <div class="bg-primary/10 border border-primary/20 rounded-lg mb-3 text-sm text-primary-700 overflow-hidden">
      <button @click="showHelp = !showHelp" class="w-full flex justify-between items-center p-2.5 hover:bg-primary/10 transition-colors cursor-pointer">
        <span class="font-semibold flex items-center"><i class="pi pi-info-circle mr-2"></i> How to build a Broadsheet Template</span>
        <i :class="showHelp ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-xs" />
      </button>
      <div v-if="showHelp" class="px-4 pb-4 border-t border-primary/20 pt-3 space-y-4">
        <p>
          A broadsheet is a class-wide report showing every student's scores across all their subjects or courses in a single table.
          Each column you add here defines what appears on that report.
        </p>

        <div>
          <h5 class="font-semibold mb-1">Column Types</h5>
          <div class="space-y-2 ml-1">
            <div>
              <span class="font-medium text-primary-800">STUDENT_INFO</span> &mdash; Shows student profile data. Use these keys:
              <ul class="list-disc list-inside ml-3 text-primary-700 mt-0.5">
                <li><code class="bg-primary/10 px-1 rounded">student_name</code> &mdash; Full name</li>
                <li><code class="bg-primary/10 px-1 rounded">matric_no</code> &mdash; Admission / Matriculation number</li>
                <li><code class="bg-primary/10 px-1 rounded">sn</code> &mdash; Serial number (auto-numbered)</li>
                <li v-if="isTertiary"><code class="bg-primary/10 px-1 rounded">tur</code> &mdash; Total Units Registered (sum of course credit units)</li>
              </ul>
            </div>
            <div>
              <span class="font-medium text-primary-800">DYNAMIC_SUBJECTS</span> &mdash;
              Automatically creates one column for every {{ isTertiary ? 'course' : 'subject' }} the students are enrolled in.
              The <strong>Source Key</strong> tells the system which value to pull from your Result Template
              (e.g. <code class="bg-primary/10 px-1 rounded">total</code> for total score,
              <code class="bg-primary/10 px-1 rounded">exam</code> for exam score only).
              You can use the picker below the field to browse your Result Templates and select a column.
              <span class="block mt-1 text-primary text-xs">Tip: If you add multiple DYNAMIC_SUBJECTS columns with different source keys (e.g. one for 'ca' and one for 'exam'), they will be grouped under each {{ isTertiary ? 'course' : 'subject' }} name.</span>
            </div>
            <div>
              <span class="font-medium text-primary-800">STUDENT_PERCENTAGE</span> &mdash;
              Calculates the student's average percentage across all {{ isTertiary ? 'courses' : 'subjects' }}.
              Set the <strong>Source Key</strong> to the score column to average (e.g. <code class="bg-primary/10 px-1 rounded">total</code>)
              and set <strong>Max Score</strong> to the maximum possible score (e.g. 100).
            </div>
            <div>
              <span class="font-medium text-primary-800">STUDENT_POSITION</span> &mdash;
              Shows the student's rank in the class. Set the <strong>Source Key</strong> to the score to rank by
              (e.g. <code class="bg-primary/10 px-1 rounded">total</code>) and choose whether to rank by
              <strong>Sum</strong> (total of all {{ isTertiary ? 'courses' : 'subjects' }}) or
              <strong>Average</strong>.
            </div>
            <div>
              <span class="font-medium text-primary-800">CALCULATION</span> &mdash;
              Runs a function over other column keys.
              <ul class="list-disc list-inside ml-3 text-primary-700 mt-0.5">
                <li><strong>SUM</strong> &mdash; Adds the values of the listed keys</li>
                <li><strong>AVERAGE</strong> &mdash; Averages the values of the listed keys</li>
                <li><strong>PERCENTAGE</strong> &mdash; Calculates a percentage from the listed keys</li>
              </ul>
              <span class="text-primary text-xs">Enter comma-separated keys in the "Keys" field, e.g. <code class="bg-primary/10 px-1 rounded">total</code></span>
            </div>
            <div>
              <span class="font-medium text-primary-800">STATIC_TEXT</span> &mdash; A fixed column (e.g. a remark or label). The value is the same for all students.
            </div>
          </div>
        </div>

        <div class="bg-primary/10 rounded p-3">
          <h5 class="font-semibold mb-1">Quick Start</h5>
          <p class="mb-1">A typical {{ isTertiary ? 'tertiary' : 'secondary school' }} broadsheet needs:</p>
          <ol class="list-decimal list-inside space-y-0.5 ml-1">
            <li v-if="!isTertiary"><strong>Student Name</strong> (STUDENT_INFO, key: <code class="bg-primary/10 px-1 rounded">student_name</code>)</li>
            <li v-if="!isTertiary"><strong>Admission No</strong> (STUDENT_INFO, key: <code class="bg-primary/10 px-1 rounded">matric_no</code>)</li>
            <li v-if="!isTertiary"><strong>Subjects</strong> (DYNAMIC_SUBJECTS, source key: <code class="bg-primary/10 px-1 rounded">total</code>)</li>
            <li v-if="!isTertiary"><strong>Percentage</strong> (STUDENT_PERCENTAGE, source key: <code class="bg-primary/10 px-1 rounded">total</code>, max: 100)</li>
            <li v-if="!isTertiary"><strong>Position</strong> (STUDENT_POSITION, source key: <code class="bg-primary/10 px-1 rounded">total</code>)</li>
            <li v-if="isTertiary"><strong>Matric No</strong> (STUDENT_INFO, key: <code class="bg-primary/10 px-1 rounded">matric_no</code>)</li>
            <li v-if="isTertiary"><strong>Student Name</strong> (STUDENT_INFO, key: <code class="bg-primary/10 px-1 rounded">student_name</code>)</li>
            <li v-if="isTertiary"><strong>TUR</strong> (STUDENT_INFO, key: <code class="bg-primary/10 px-1 rounded">tur</code>) &mdash; total credit units registered</li>
            <li v-if="isTertiary"><strong>Courses</strong> (DYNAMIC_SUBJECTS, source key: <code class="bg-primary/10 px-1 rounded">total</code>)</li>
          </ol>
          <p class="mt-2 text-xs text-primary">The default columns are pre-filled when you create a new template. Adjust them to match your school's result template.</p>
        </div>
      </div>
    </div>

    <div class="mb-1.5 flex justify-between items-center">
      <h3 class="font-semibold text-sm">Columns (Drag to Reorder)</h3>
      <Button label="Add Column" icon="pi pi-plus" size="small" outlined @click="addColumn" />
    </div>

    <!-- Header Row -->
    <div class="grid grid-cols-12 gap-2 px-3 py-1.5 bg-gray-100 font-semibold text-xs text-gray-600 rounded-t-md border-b border-gray-200">
       <div class="col-span-1 text-center">#</div>
       <div class="col-span-3">Label</div>
       <div class="col-span-3">Key</div>
       <div class="col-span-3">Type</div>
       <div class="col-span-1 text-center">Vis.</div>
       <div class="col-span-1 text-center">Act.</div>
    </div>

    <div class="border border-gray-200 rounded-b-md bg-white min-h-[80px]" @dragover.prevent @drop="onDrop">
       <div v-if="form.columns.length === 0" class="text-center py-4 text-gray-500 italic text-sm">
          No columns added. Click "Add Column" to start.
       </div>

       <div v-for="(item, index) in form.columns" :key="item.tempId" draggable="true"
            @dragstart="onDragStart(index, $event)" @dragenter="onDragEnter(index)" @dragend="onDragEnd"
            class="flex flex-col w-full border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors cursor-move"
            :class="{
               'opacity-50 dashed border-2 border-primary': draggedIndex === index,
               'border-t-4 border-primary': dropTargetIndex === index && draggedIndex !== index
            }">

          <!-- Main Row -->
          <div class="grid grid-cols-12 gap-2 items-center p-1.5">
            <!-- Index / Drag Handle -->
            <div class="col-span-1 flex items-center justify-center text-gray-400 font-bold">
               <i class="pi pi-bars mr-2 text-gray-300"></i> {{ index + 1 }}
            </div>

            <!-- Label -->
            <div class="col-span-3">
               <InputText v-model="item.label" class="w-full text-sm p-1.5" placeholder="Label" />
            </div>

            <!-- Key -->
            <div class="col-span-3">
               <InputText v-model="item.key" class="w-full text-sm p-1.5 font-mono" placeholder="key" />
            </div>

            <!-- Type -->
            <div class="col-span-3">
               <Select v-model="item.type" :options="['STUDENT_INFO', 'DYNAMIC_SUBJECTS', 'STUDENT_PERCENTAGE', 'STUDENT_POSITION', 'CALCULATION', 'STATIC_TEXT']" class="w-full text-sm" placeholder="Type" />
            </div>

            <!-- Visible -->
            <div class="col-span-1 flex justify-center">
               <Checkbox v-model="item.is_visible" :binary="true" />
            </div>

            <!-- Actions -->
            <div class="col-span-1 flex justify-center">
               <Button icon="pi pi-trash" text rounded severity="danger" size="small" @click="removeColumn(index)" />
            </div>
          </div>

          <!-- Helper / Settings Rows -->
          <div v-if="item.type === 'DYNAMIC_SUBJECTS'" class="px-8 pb-1.5 pt-0 grid grid-cols-1 gap-2 bg-yellow-50/50 inner-shadow">
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-24">Source Key:</span>
                <InputText v-model="item.calculation_arguments_str" class="w-full text-sm p-1.5" placeholder="e.g. total, exam" @blur="updateDynamicArgs(item)" disabled />
             </div>
              <!-- Helper to pick from existing templates -->
             <div class="bg-yellow-100/50 p-2 rounded-md border border-yellow-200 mt-1">
                <div class="flex gap-2 items-center">
                    <span class="text-[10px] uppercase font-bold text-yellow-800 whitespace-nowrap">Or Pick:</span>
                    <Select v-model="item.selected_source_template_id" :options="availableSourceTemplates"
                        optionLabel="name" optionValue="id" placeholder="Select Result Template"
                        class="w-48 text-xs" />

                    <Select v-if="item.selected_source_template_id" v-model="item.selected_source_column_key"
                        :options="allTemplates.find(t => t.id === item.selected_source_template_id)?.columns || []"
                        optionLabel="label" optionValue="key" placeholder="Select Column"
                        class="w-48 text-xs"
                        @change="(e: SelectChangeEvent) => { item.calculation_arguments_str = e.value; updateDynamicArgs(item); }" />
                </div>
             </div>
             <small class="text-gray-400 text-[10px]">Key from Result Template (e.g. 'total')</small>
          </div>

          <div v-if="item.type === 'CALCULATION'" class="px-8 pb-1.5 pt-0 grid grid-cols-2 gap-2 bg-gray-50/50 inner-shadow">
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-16">Function:</span>
                <Select v-model="item.calculation_function" :options="['SUM', 'AVERAGE', 'PERCENTAGE']" class="w-full text-sm" />
             </div>
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-16">Keys:</span>
                <InputText v-model="item.calculation_arguments_str" class="w-full text-sm p-1.5" placeholder="key1, key2" @blur="updateArgs(item)" />
             </div>
          </div>

          <!-- STUDENT_PERCENTAGE Config -->
          <div v-if="item.type === 'STUDENT_PERCENTAGE'" class="px-8 pb-1.5 pt-0 grid grid-cols-2 gap-2 bg-primary/10 inner-shadow">
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-24">Source Key:</span>
                <InputText v-model="item.calculation_arguments_str" class="w-full text-sm p-1.5" placeholder="e.g. total" @blur="updatePercentageArgs(item)" />
             </div>
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-24">Max Score:</span>
                <InputText v-model="item.max_score" class="w-full text-sm p-1.5" placeholder="100" type="number" @blur="updatePercentageArgs(item)" />
             </div>
             <small class="col-span-2 text-gray-400 text-[10px]">Shows student's average percentage across all subjects</small>
          </div>

          <!-- STUDENT_POSITION Config -->
          <div v-if="item.type === 'STUDENT_POSITION'" class="px-8 pb-1.5 pt-0 grid grid-cols-2 gap-2 bg-purple-50/50 inner-shadow">
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-24">Source Key:</span>
                <InputText v-model="item.calculation_arguments_str" class="w-full text-sm p-1.5" placeholder="e.g. total" @blur="updatePositionArgs(item)" />
             </div>
             <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-500 w-24">Aggregate:</span>
                <Select v-model="item.aggregate" :options="['sum', 'average']" class="w-full text-sm" placeholder="sum" @change="updatePositionArgs(item)" />
             </div>
             <small class="col-span-2 text-gray-400 text-[10px]">Shows student's class rank based on the source key</small>
          </div>
       </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { type SelectChangeEvent } from 'primevue/select';
import { useDragDropList } from '@/composables/common/useDragDropList';

import {
  useResultTemplatesQuery,
  useCreateBroadsheetTemplateMutation,
  useUpdateBroadsheetTemplateMutation,
} from '@/types/vue-apollo-helpers';
import type { BroadsheetTemplate, BroadsheetTemplateColumn, ResultTemplate } from '@/types/composition-functions';

interface ColumnFormItem {
  tempId: number;
  id?: string | null;
  label: string;
  key: string;
  type: string;
  is_visible: boolean;
  calculation_function: string | null;
  calculation_arguments: Record<string, unknown> | unknown[];
  calculation_arguments_str: string;
  max_score?: string | number;
  aggregate?: string;
  selected_source_template_id?: string;
  selected_source_column_key?: string;
}

const props = defineProps<{
  initialData?: BroadsheetTemplate;
  isClone?: boolean;
}>();

const emit = defineEmits(['cancel', 'saved']);
const toast = useToast();

const isEditing = computed(() => !!props.initialData && !props.isClone);
const showHelp = ref(false);

// Queries
const { result: templateResult } = useResultTemplatesQuery();
const allTemplates = computed<ResultTemplate[]>(() => templateResult.value?.resultTemplates?.data || []);
const availableSourceTemplates = computed(() => allTemplates.value);

const form = ref({
  id: null as string | null,
  name: '',
  description: '',
  columns: [] as ColumnFormItem[],
  enableSummary: false,
  summaryPercentageKey: '',
  summaryMaxScore: 100,
  useCourseAbbreviationInHeaders: false,
});

import { useSchoolFeatures } from '@/composables/useSchoolFeatures';

const { isTertiary } = useSchoolFeatures();

onMounted(() => {
  if (props.initialData) {
    form.value.name = props.initialData.name + (props.isClone ? ' (Copy)' : '');
    form.value.description = props.initialData.description;
    form.value.id = props.isClone ? null : props.initialData.id;

    // Load summary settings from properties
    const summaryProps = props.initialData.properties?.summary;
    if (summaryProps) {
      form.value.enableSummary = true;
      form.value.summaryPercentageKey = summaryProps.percentage_key || '';
      form.value.summaryMaxScore = summaryProps.max_score || 100;
    }

    if (props.initialData.properties?.use_course_abbreviation_in_headers) {
      form.value.useCourseAbbreviationInHeaders = true;
    }

    // Deep copy columns and add tempId for OrderList
    form.value.columns = (props.initialData.columns || []).map((c: BroadsheetTemplateColumn, idx: number) => ({
      ...c,
      id: props.isClone ? null : c.id, // clear ID if clone
      tempId: Date.now() + idx,
      calculation_arguments_str: Array.isArray(c.calculation_arguments?.keys)
        ? c.calculation_arguments.keys.join(', ')
        : (c.calculation_arguments?.source_key || ''),
      max_score: c.calculation_arguments?.max_score,
      aggregate: c.calculation_arguments?.aggregate,
    }));
  } else {
    // Pre-populate defaults (Aligned with Master Plan)
    const defaults = [
      {
        tempId: Date.now(),
        label: 'Full Name',
        key: 'student_name',
        type: 'STUDENT_INFO',
        is_visible: true,
        calculation_function: null,
        calculation_arguments: [],
        calculation_arguments_str: '',
      },
      {
        tempId: Date.now() + 1,
        label: 'Admission No',
        key: 'matric_no',
        type: 'STUDENT_INFO',
        is_visible: true,
        calculation_function: null,
        calculation_arguments: [],
        calculation_arguments_str: '',
      },
    ];

    if (isTertiary.value) {
      defaults.push({
        tempId: Date.now() + 2,
        label: 'Total Units Registered',
        key: 'tur',
        type: 'STUDENT_INFO',
        is_visible: true,
        calculation_function: null,
        calculation_arguments: [],
        calculation_arguments_str: '',
      });
      // Add dynamic courses
      defaults.push({
        tempId: Date.now() + 3,
        label: 'Courses',
        key: 'courses', // Generic key
        type: 'DYNAMIC_SUBJECTS',
        is_visible: true,
        calculation_function: null,
        calculation_arguments: { source_key: 'total' }, // Default to Total Score
        calculation_arguments_str: 'total',
      });
    } else {
      defaults.push({
        tempId: Date.now() + 2,
        label: 'Subjects',
        key: 'subjects', // Generic key
        type: 'DYNAMIC_SUBJECTS',
        is_visible: true,
        calculation_function: null,
        calculation_arguments: { source_key: 'total' }, // Default to Total Score
        calculation_arguments_str: 'total',
      });

      defaults.push({
        tempId: Date.now() + 3,
        label: 'Class Average',
        key: 'avg',
        type: 'CALCULATION',
        is_visible: true,
        calculation_function: 'AVERAGE',
        calculation_arguments: { keys: ['total'] },
        calculation_arguments_str: 'total',
      });
    }

    form.value.columns = defaults;
  }
});

const addColumn = () => {
  form.value.columns.push({
    tempId: Date.now(),
    label: 'New Column',
    key: 'new_col',
    type: 'STUDENT_INFO',
    is_visible: true,
    calculation_function: null,
    calculation_arguments: [],
    calculation_arguments_str: '',
  });
};

const removeColumn = (index: number) => {
  form.value.columns.splice(index, 1);
};

const updateArgs = (item: ColumnFormItem) => {
  if (item.calculation_arguments_str) {
    item.calculation_arguments = {
      keys: item.calculation_arguments_str.split(',').map((s: string) => s.trim()),
    };
  } else {
    item.calculation_arguments = { keys: [] };
  }
};

const updateDynamicArgs = (item: Record<string, unknown>) => {
  item.calculation_arguments = {
    source_key: (item.calculation_arguments_str as string) || 'total',
  };
};

const updatePercentageArgs = (item: ColumnFormItem) => {
  item.calculation_arguments = {
    source_key: item.calculation_arguments_str || 'total',
    max_score: parseInt(item.max_score as string) || 100,
  };
};

const updatePositionArgs = (item: ColumnFormItem) => {
  item.calculation_arguments = {
    source_key: item.calculation_arguments_str || 'total',
    aggregate: item.aggregate || 'sum',
  };
};

// Drag and Drop Logic - using shared composable
const columnsRef = computed(() => form.value.columns);
const {
  draggedIndex,
  dropTargetIndex,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
} = useDragDropList(columnsRef);

// Mutations
const { mutate: createTemplate, loading: creating } = useCreateBroadsheetTemplateMutation();
const { mutate: updateTemplate, loading: updating } = useUpdateBroadsheetTemplateMutation();

const saving = computed(() => creating.value || updating.value);

const save = async() => {
  // Validate
  if (!form.value.name) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Name is required' });
    return;
  }

  // Prepare input. Sequence is index + 1
  const typesWithArgs = ['CALCULATION', 'DYNAMIC_SUBJECTS', 'STUDENT_PERCENTAGE', 'STUDENT_POSITION'];
  const columnsInput = form.value.columns.map((c, index) => ({
    id: c.id,
    label: c.label,
    key: c.key,
    type: c.type,
    is_visible: c.is_visible,
    sequence: index + 1,
    calculation_function: c.type === 'CALCULATION' ? c.calculation_function : null,
    calculation_arguments: typesWithArgs.includes(c.type) ? c.calculation_arguments : null,
  }));

  // Build properties object (summary config)
  const properties: Record<string, unknown> = {
    use_course_abbreviation_in_headers: form.value.useCourseAbbreviationInHeaders,
  };

  if (form.value.enableSummary) {
    properties.summary = {
      percentage_key: form.value.summaryPercentageKey || 'total',
      max_score: form.value.summaryMaxScore || 100,
    };
  }

  try {
    if (isEditing.value && form.value.id) {
      await updateTemplate({
        input: {
          id: form.value.id,
          name: form.value.name,
          description: form.value.description,
          columns: columnsInput,
          properties,
        },
      });
      toast.add({ severity: 'success', summary: 'Saved', detail: 'Template updated' });
    } else {
      await createTemplate({
        input: {
          name: form.value.name,
          description: form.value.description,
          columns: columnsInput,
          properties,
        },
      });
      toast.add({ severity: 'success', summary: 'Created', detail: 'Template created' });
    }
    emit('saved');
  } catch (e) {
    const error = e as Error;
    toast.add({ severity: 'error', summary: 'Error', detail: error.message });
  }
};
</script>
